import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Activity, 
  Globe, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RotateCcw,
  TrendingUp,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useTestRunner } from '@/hooks/use-test-runner';
import EmailNotificationTest from '@/components/admin/EmailNotificationTest';
import AvatarDebugTool from '@/components/admin/AvatarDebugTool';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

import { glassCard, glassCardStatic, glassCardLight, glassTabsList } from '@/utils/admin-glass-styles';

interface EndpointStatus {
  name: string;
  status: 'online' | 'offline' | 'slow';
  responseTime: number;
  httpStatus: number;
  lastChecked: string;
  error?: string;
}

const AdminMonitoring = () => {
  const { runTests, testRuns, testResults, isLoading, loadTestRuns } = useTestRunner();
  const { toast } = useToast();
  const [lastRunSummary, setLastRunSummary] = useState<any>(null);

  useEffect(() => {
    loadTestRuns();
  }, []);

  useEffect(() => {
    if (testRuns.length > 0) {
      const latestRun = testRuns[0];
      setLastRunSummary(latestRun.summary);
    }
  }, [testRuns]);

  const runMonitoring = async () => {
    try {
      console.log('Starting monitoring checks...');
      await runTests({ testType: 'monitoring' as any });
      toast({
        title: 'Monitoring Started',
        description: 'Running health checks on all endpoints...',
      });
    } catch (error) {
      console.error('Error running monitoring:', error);
      toast({
        title: 'Monitoring Error',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  // Parse endpoint statuses from latest test results
  const getEndpointStatuses = (): EndpointStatus[] => {
    if (!testResults || testResults.length === 0) {
      return [
        { name: 'Homepage', status: 'offline', responseTime: 0, httpStatus: 0, lastChecked: 'No tests run yet', error: 'Click "Run Health Check" to test this endpoint' },
        { name: 'About Us', status: 'offline', responseTime: 0, httpStatus: 0, lastChecked: 'No tests run yet', error: 'Click "Run Health Check" to test this endpoint' },
        { name: 'Practitioners', status: 'offline', responseTime: 0, httpStatus: 0, lastChecked: 'No tests run yet', error: 'Click "Run Health Check" to test this endpoint' },
        { name: 'Practitioner Auth', status: 'offline', responseTime: 0, httpStatus: 0, lastChecked: 'No tests run yet', error: 'Click "Run Health Check" to test this endpoint' },
        { name: 'Client Auth', status: 'offline', responseTime: 0, httpStatus: 0, lastChecked: 'No tests run yet', error: 'Click "Run Health Check" to test this endpoint' }
      ];
    }

    return testResults
      .filter(result => result.metadata?.type === 'monitoring')
      .map(result => {
        const responseTime = result.metadata?.responseTime || result.duration;
        const httpStatus = result.metadata?.httpStatus || 0;
        
        let status: 'online' | 'offline' | 'slow' = 'offline';
        if (result.status === 'passed') {
          status = responseTime > 3000 ? 'slow' : 'online';
        }

        return {
          name: result.test_name,
          status,
          responseTime,
          httpStatus,
          lastChecked: new Date(result.created_at).toLocaleString(),
          error: result.error
        };
      });
  };

  const endpointStatuses = getEndpointStatuses();
  const onlineCount = endpointStatuses.filter(e => e.status === 'online').length;
  const offlineCount = endpointStatuses.filter(e => e.status === 'offline').length;
  const slowCount = endpointStatuses.filter(e => e.status === 'slow').length;
  const averageResponseTime = endpointStatuses.length > 0 
    ? endpointStatuses.reduce((sum, e) => sum + e.responseTime, 0) / endpointStatuses.length 
    : 0;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'slow':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'offline':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'online':
        return <Badge variant="outline" className="text-green-600">Online</Badge>;
      case 'slow':
        return <Badge variant="outline" className="text-yellow-600">Slow</Badge>;
      case 'offline':
        return <Badge variant="outline" className="text-red-600">Offline</Badge>;
      default:
        return <Badge variant="outline" className="text-gray-500">Unknown</Badge>;
    }
  };

  const getResponseTimeColor = (responseTime: number) => {
    if (responseTime < 1000) return 'text-green-600';
    if (responseTime < 3000) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Website Monitoring</h1>
          <p className="text-muted-foreground/60">Monitor endpoint health and performance</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={runMonitoring}
            disabled={isLoading}
            className="flex items-center space-x-2"
          >
            <Activity className="h-4 w-4" />
            <span>{isLoading ? 'Checking...' : 'Run Health Check'}</span>
          </Button>
          <Button variant="outline">
            <RotateCcw className="h-4 w-4 mr-2" />
            Clear History
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Wifi className="h-4 w-4 mr-2 text-green-500" />
              Online
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{onlineCount}</div>
            <p className="text-xs text-muted-foreground/60">endpoints working</p>
          </CardContent>
        </Card>
        
        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <WifiOff className="h-4 w-4 mr-2 text-red-500" />
              Offline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-red-600">{offlineCount}</div>
            <p className="text-xs text-muted-foreground/60">endpoints down</p>
          </CardContent>
        </Card>

        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Avg Response
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl sm:text-3xl font-bold ${getResponseTimeColor(averageResponseTime)}`}>
              {averageResponseTime.toFixed(0)}ms
            </div>
            <p className="text-xs text-muted-foreground/60">average time</p>
          </CardContent>
        </Card>

        <Card className={glassCard}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <TrendingUp className="h-4 w-4 mr-2 text-yellow-500" />
              Slow
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{slowCount}</div>
            <p className="text-xs text-muted-foreground/60">slow responses</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="status" className="w-full">
        <TabsList className={glassTabsList}>
          <TabsTrigger value="status">Endpoint Status</TabsTrigger>
          <TabsTrigger value="history">Monitoring History</TabsTrigger>
          <TabsTrigger value="debug">Debug Tools</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="space-y-4">
          <div className="grid gap-4">
            {endpointStatuses.map((endpoint) => (
              <Card key={endpoint.name} className={glassCardLight}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(endpoint.status)}
                      <div>
                        <CardTitle className="text-lg">{endpoint.name}</CardTitle>
                        <CardDescription className="text-muted-foreground/60">
                          Last checked: {endpoint.lastChecked}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {getStatusBadge(endpoint.status)}
                      <span className={`text-sm font-medium ${getResponseTimeColor(endpoint.responseTime)}`}>
                        {endpoint.responseTime}ms
                      </span>
                      {endpoint.httpStatus > 0 && (
                        <Badge variant="secondary">
                          HTTP {endpoint.httpStatus}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                 {endpoint.error && (
                   <CardContent>
                     <div className={`p-3 border rounded-md ${
                       endpoint.lastChecked === 'No tests run yet' 
                         ? 'bg-blue-50/80 border-blue-200/60' 
                         : 'bg-red-50/80 border-red-200/60'
                     }`}>
                       <p className={`text-sm font-medium ${
                         endpoint.lastChecked === 'No tests run yet' 
                           ? 'text-blue-800' 
                           : 'text-red-800'
                       }`}>
                         {endpoint.lastChecked === 'No tests run yet' ? 'Info:' : 'Error:'}
                       </p>
                       <p className={`mt-1 text-sm ${
                         endpoint.lastChecked === 'No tests run yet' 
                           ? 'text-blue-700' 
                           : 'text-red-700'
                       }`}>
                         {endpoint.error}
                       </p>
                     </div>
                   </CardContent>
                 )}
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card className={glassCardStatic}>
            <CardHeader>
              <CardTitle>Monitoring History</CardTitle>
              <CardDescription className="text-muted-foreground/60">Recent monitoring check results</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96 w-full rounded-md border border-white/20 p-4">
                {!testResults || testResults.length === 0 ? (
                  <p className="text-muted-foreground/60 text-center py-8">No monitoring data available</p>
                ) : (
                  <div className="space-y-1">
                    {testResults
                      .filter(result => result.metadata?.type === 'monitoring')
                      .map((result) => (
                      <div key={result.id} className="text-sm font-mono border-b border-white/10 pb-2">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{result.test_name}</span>
                          <span className={`${result.status === 'passed' ? 'text-green-600' : 'text-red-600'}`}>
                            {result.status === 'passed' ? '✓' : '✗'}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground/60">
                          {new Date(result.created_at).toLocaleString()} • {result.duration}ms
                          {result.metadata?.httpStatus && ` • HTTP ${result.metadata.httpStatus}`}
                        </div>
                        {result.error && (
                          <div className="text-xs text-red-500 mt-1">{result.error}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="debug" className="space-y-6">
          <div className="grid gap-6">
            <EmailNotificationTest />
            <AvatarDebugTool />
          </div>
        </TabsContent>

        <TabsContent value="config">
          <Card className={glassCardStatic}>
            <CardHeader>
              <CardTitle>Monitoring Configuration</CardTitle>
              <CardDescription className="text-muted-foreground/60">Current monitoring settings and endpoints</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Base URL</label>
                  <p className="text-sm text-muted-foreground/60">https://resonow.org</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Check Interval</label>
                  <p className="text-sm text-muted-foreground/60">Manual / On-demand</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Timeout</label>
                  <p className="text-sm text-muted-foreground/60">30 seconds</p>
                </div>
                <div>
                  <label className="text-sm font-medium">User Agent</label>
                  <p className="text-sm text-muted-foreground/60">ResoNow-Monitor/1.0</p>
                </div>
              </div>
              
              <div className="mt-6">
                <label className="text-sm font-medium">Monitored Endpoints</label>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <Globe className="h-3 w-3" />
                    <span>/ (Homepage)</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Globe className="h-3 w-3" />
                    <span>/about-us (About Us)</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Globe className="h-3 w-3" />
                    <span>/practitioners (Practitioners)</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Globe className="h-3 w-3" />
                    <span>/practitioner-auth (Practitioner Login)</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Globe className="h-3 w-3" />
                    <span>/client-auth (Client Login)</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-3 bg-blue-50/80 border border-blue-200/60 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> This is a simplified monitoring system that checks HTTP endpoint availability and response times. 
                  For advanced monitoring features, consider integrating with a dedicated monitoring service.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminMonitoring;
